import React from "react";
import { Box, Tooltip, Typography, useTheme } from "@mui/material";
import { alpha, keyframes } from "@mui/material/styles";
import TipsAndUpdatesIcon from "@mui/icons-material/TipsAndUpdates";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";

const glow = keyframes`
  0%, 100% { opacity: 0.55; }
  50% { opacity: 1; }
`;

const AIEvidenceChip = ({
  pendingCount = 0,
  approvedCount = 0,
  myPendingCount = 0,
  ineligible = false,
}) => {
  const theme = useTheme();
  const tc = theme.customColors;
  const info = tc.status.info;

  if (ineligible && !pendingCount && !approvedCount) {
    return (
      <Tooltip
        arrow
        title="All checks in this control are automated, so Rego AI evidence is not applicable"
      >
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            px: 1,
            py: 0.35,
            borderRadius: "999px",
            border: `1px solid ${tc.border.subtle}`,
            backgroundColor: tc.background.light,
            opacity: 0.7,
            cursor: "default",
          }}
        >
          <Typography
            sx={{
              fontSize: "11.5px",
              fontWeight: 500,
              color: tc.text.disabled,
              lineHeight: 1.4,
            }}
          >
            Automated
          </Typography>
        </Box>
      </Tooltip>
    );
  }

  if (!pendingCount && !approvedCount) {
    return (
      <Typography sx={{ fontSize: "13px", color: tc.text.disabled }}>
        —
      </Typography>
    );
  }

  if (pendingCount > 0) {
    // Some of the pending suggestions are on integrations this user owns, so
    // they are the one who can action them — mark the chip as theirs.
    const mine = Math.min(myPendingCount, pendingCount);
    const isMine = mine > 0;
    const isPartlyMine = isMine && mine < pendingCount;

    const mineLabel = isPartlyMine
      ? `${mine} of ${pendingCount} awaiting your review`
      : `${pendingCount} awaiting your review`;
    const baseLabel = isMine
      ? mineLabel
      : `${pendingCount} AI evidence suggestion${
          pendingCount === 1 ? "" : "s"
        } awaiting review`;

    return (
      <Tooltip
        arrow
        title={
          approvedCount > 0
            ? `${baseLabel} · ${approvedCount} already approved`
            : baseLabel
        }
      >
        <Box
          sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}
        >
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.65,
              px: 1,
              py: 0.35,
              borderRadius: "999px",
              border: `1px solid ${alpha(info.main, isMine ? 0.45 : 0.28)}`,
              background: isMine
                ? `linear-gradient(135deg, ${alpha(info.main, 0.2)} 0%, ${alpha(
                    info.main,
                    0.08,
                  )} 100%)`
                : `linear-gradient(135deg, ${alpha(
                    info.main,
                    0.12,
                  )} 0%, ${alpha(info.main, 0.04)} 100%)`,
              cursor: "default",
            }}
          >
            <TipsAndUpdatesIcon
              sx={{
                fontSize: 13,
                color: info.main,
                animation: `${glow} 2.6s ease-in-out infinite`,
              }}
            />
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: isMine ? 700 : 600,
                color: info.text,
                fontVariantNumeric: "tabular-nums",
                lineHeight: 1.4,
              }}
            >
              {pendingCount}
            </Typography>
          </Box>
          {/* Action dot: present only when some of these are the viewer's to
              review. Trails the chip so the column stays left-aligned. */}
          {isMine && (
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                backgroundColor: info.main,
                flexShrink: 0,
              }}
            />
          )}
        </Box>
      </Tooltip>
    );
  }

  return (
    <Tooltip
      arrow
      title={`${approvedCount} AI suggestion${
        approvedCount === 1 ? "" : "s"
      } approved as evidence`}
    >
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          px: 0.9,
          py: 0.35,
          borderRadius: "999px",
          border: `1px solid ${alpha(tc.button.success.main, 0.28)}`,
          backgroundColor: alpha(tc.button.success.main, 0.08),
          cursor: "default",
        }}
      >
        <CheckCircleRoundedIcon
          sx={{ fontSize: 12, color: tc.button.success.main }}
        />
        <Typography
          sx={{
            fontSize: "12px",
            fontWeight: 600,
            color: tc.button.success.dark,
            fontVariantNumeric: "tabular-nums",
            lineHeight: 1.4,
          }}
        >
          {approvedCount}
        </Typography>
      </Box>
    </Tooltip>
  );
};

export default AIEvidenceChip;
